import React, { useState, useEffect, useCallback } from "react";
import ForemanMetric from "../components/ForemanMetric";
import moment from "moment";
import { useGetMetrics } from "../apiCalls/userApiCalls";
import { useSelector } from "react-redux";
import Loader from "../components/Loader";
import SalesMetric from "../components/SalesMetric";
import SkeletonMetric from "../components/SkeletonMetric";
import { Link } from "react-router-dom";

function Home() {
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "months").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [type, setType] = useState("foreman");
  const [page, setPage] = useState(1);
  const [limit] = useState(1);
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [processedPages, setProcessedPages] = useState(new Set());
  const [overtimeMethod, setOvertimeMethod] = useState(null);
  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;

  const { isLoading, data } = useGetMetrics(token, type, startDate, endDate, page, limit);

  // Reset data when filters change
  useEffect(() => {
    setAllData([]);
    setPage(1);
    setHasMore(true);
    setProcessedPages(new Set());
    setOvertimeMethod(null);
  }, [type, startDate, endDate]);

  // Update allData when new data arrives
  useEffect(() => {
    if (data?.data?.data && !processedPages.has(page)) {
      setAllData(prevData => [...prevData, ...data.data.data]);
      setHasMore(page < data.data.pagination.totalPages);
      setProcessedPages(prev => new Set([...prev, page]));

      // Capture overtime calculation method from first response
      if (overtimeMethod === null && data.data.overtimeCalculationMethod) {
        setOvertimeMethod(data.data.overtimeCalculationMethod);
      }
    }
  }, [data, page, processedPages, overtimeMethod]);

  // Auto-load next page when current page finishes loading and has more data
  useEffect(() => {
    if (!isLoading && hasMore && data?.data?.data && processedPages.has(page)) {
      setPage(prevPage => prevPage + 1);
    }
  }, [isLoading, hasMore, data, page, processedPages]);

  // Render skeleton loaders
  const renderSkeletons = (count) => {
    return Array(count).fill(0).map((_, index) => (
      <div key={`skeleton-${index}`} className="m-2">
        <SkeletonMetric type={type} />
      </div>
    ));
  };

  return (
    <>
      <div className="mt-24 flex gap-2 py-4 justify-center ">
        <select
          name=""
          id=""
          className="rounded-md p-1 border border-gray-300 bg-white"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
          }}
        >
          <option value="foreman">Foreman</option>
          <option value="sales">Sales</option>
        </select>
        <input
          type="date"
          name="startDate"
          id="startDate"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
          className="rounded-md p-1 w-32 border border-gray-300 bg-white"
        />
        <input
          type="date"
          name="endDate"
          id="endDate"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
          className="rounded-md p-1 w-32 border border-gray-300 bg-white"
        />
      </div>
      <div className="flex flex-wrap gap-4 justify-center">
        {type === "foreman" ? (
          <>
            {allData.map((item, index) => (
              <Link 
                key={item._id} 
                to={`/history/${item._id}`}
              >
                <ForemanMetric
                  budget={item.totalBudget}
                  labor={item.totalLaborCost}
                  nrexpense={item.totalNRExpense}
                  potentialBonus={item.totalUnrealizedBonus}
                  img={item.imgUrl}
                  name={item.name}
                  retail={item.totalRetail}
                  hours={item.totalHours}
                  dolAdditionalOT={item.totalDolAdditionalOT}
                  showDolOT={overtimeMethod === "dol"}
                />
              </Link>
            ))}
            {isLoading && renderSkeletons(1)}
          </>
        ) : type === "sales" ? (
          <>
            {allData.map((item, index) => (
              <Link 
                key={item._id} 
                to={`/history/${item._id}`}
              >
                <SalesMetric
                  jobs={item.jobs}
                  amount={item.amount}
                  bonus={item.bonus}
                  img={item.imgUrl}
                  name={item.name}
                />
              </Link>
            ))}
            {isLoading && renderSkeletons(1)}
          </>
        ) : null}
      </div>

    </>
  );
}

export default Home;
